<!--
@component
RootLayout

Root layout. Tracks window size in appState and starts the data Poller.
-->

<script lang="ts">
  import { onMount } from 'svelte';
  import type { Snippet } from 'svelte';
  import { Poller, getData } from '$utils/data';
  import { appState } from '$lib/stores/index.svelte';
  import { page, updated } from '$app/state';
  import { beforeNavigate } from '$app/navigation';
  import { browser } from '$app/environment';
  import {
    reFetchUpdateSTLF,
    MetaAndHeaders,
  } from '$lib/components/ST/index.js';

  interface Props {
    children: Snippet;
  }

  let { children }: Props = $props();

  let innerWidth = $state(0);

  $effect(() => {
    appState.windowWidth = innerWidth;
  });

  let poller = $state<Poller | null>(null);

  onMount(() => {
    reFetchUpdateSTLF();
  });

  // `appState.optaData` is seeded by `+layout.ts` before this component
  // mounts. Here we only start the background `Poller` that refreshes it
  // on an interval (see src/utils/data/poller.js).
  $effect(() => {
    poller = new Poller((newData) => {
      appState.optaData = newData;
    });
    return () => poller?.destroy();
  });

  $effect(() => {
    const pageParams = browser && page?.url?.searchParams;
    appState.isLoading = !pageParams;
  });

  // Mirror SvelteKit's URL into the store so derived values (and the
  // few utils that need it during SSR) can read it without subscribing
  // to `page` themselves. Lives in the root layout so both the app and
  // embed groups get it.
  $effect(() => {
    appState.currentUrl = page.url ?? null;
  });

  // Force a hard reload on the next link click if SvelteKit has noticed a
  // new version (via `kit.version.pollInterval` in `svelte.config.js`).
  // Without this, a stale tab would keep doing soft client-side navs into
  // chunk hashes that no longer exist on the CDN and 500. See issue #70.
  beforeNavigate((nav) => {
    if (!nav.willUnload && nav.to && updated.current) {
      nav.cancel();
      location.href = nav.to.url.href;
    }
  });

  // The fixed `kit.version.pollInterval` only fires on a timer, so a tab
  // that's been hidden in the background for hours (laptop closed, app
  // backgrounded, etc.) won't notice a new publish until the next tick —
  // which can easily be after the user has already clicked a link.
  //
  // When the tab becomes visible / regains focus, *await* the version
  // check and reload the tab immediately if it's stale. We can't just
  // fire-and-forget here because the user can click a link before the
  // promise resolves, which races `beforeNavigate` and produces the
  // same 500. Reloading the whole tab on visibility is fine for a news
  // graphic — there's no editable state to preserve.
  $effect(() => {
    if (!browser) return;
    let reloading = false;
    const check = async () => {
      if (document.visibilityState !== 'visible') return;
      if (reloading) return;
      try {
        const stale = await updated.check();
        if (stale) {
          reloading = true;
          location.reload();
        }
      } catch {
        // Network blip on resume — next poll tick or next interaction
        // will retry. No-op here so we don't reload on an offline tab.
      }
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    // Run once on mount to catch tabs that were already stale when this
    // build first loaded (e.g. opened via `bfcache` restore).
    void check();
    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
    };
  });

  // Final-resort guard: if a soft navigation still tries to import a
  // chunk that no longer exists on the CDN (race between visibility
  // check, polling tick, and user click), the dynamic import rejects
  // with a `ChunkLoadError`-shaped error. Catch it on `window.error`
  // and full-reload so the user gets the new build instead of the
  // generic SvelteKit 500.
  $effect(() => {
    if (!browser) return;
    const isChunkError = (msg: unknown) =>
      typeof msg === 'string' &&
      /chunk|importing a module|Failed to fetch dynamically imported/i.test(
        msg
      );
    const onError = (e: ErrorEvent) => {
      if (isChunkError(e.message)) location.reload();
    };
    const onUnhandled = (e: PromiseRejectionEvent) => {
      const reason = e.reason;
      const msg =
        reason instanceof Error ? reason.message : String(reason ?? '');
      if (isChunkError(msg)) location.reload();
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandled);
    };
  });
</script>

<svelte:window bind:innerWidth />

{@render children()}

<MetaAndHeaders />
